/**
 * ToggleSwitch Web Component
 * A sliding toggle switch with smooth animations
 * 
 * Usage:
 *   <script src="toggle-switch.js"></script>
 *   <toggle-switch></toggle-switch>
 *   
 *   <!-- With initial state -->
 *   <toggle-switch checked></toggle-switch>
 *   
 *   <!-- With labels -->
 *   <toggle-switch label-left="Off" label-right="On"></toggle-switch>
 *   
 *   <!-- Disabled -->
 *   <toggle-switch disabled></toggle-switch>
 *   
 *   <!-- Different sizes -->
 *   <toggle-switch size="small"></toggle-switch>
 *   <toggle-switch size="large"></toggle-switch>
 * 
 * Attributes:
 *   checked      - If present, switch is in the "on" (right) position
 *   disabled     - If present, switch cannot be toggled
 *   label-left   - Label displayed on the left side
 *   label-right  - Label displayed on the right side
 *   size         - "small", "medium" (default), or "large"
 *   name         - Form field name (for form submission)
 *   value        - Value when checked (default: "on")
 * 
 * Events:
 *   change       - Fired when toggle state changes. Detail: { checked: boolean }
 * 
 * Properties:
 *   checked      - Get/set the checked state
 *   disabled     - Get/set the disabled state
 *   toggle()     - Toggle the switch programmatically
 * 
 * CSS Custom Properties:
 *   --toggle-track-color-off     - Track color when off (default: #d4d0c8)
 *   --toggle-track-color-on      - Track color when on (default: #8b7355)
 *   --toggle-thumb-color         - Thumb color (default: #fff)
 *   --toggle-thumb-shadow        - Thumb box-shadow
 *   --toggle-transition-duration - Animation duration (default: 0.2s)
 */

(function() {
  'use strict';

  const STYLES = `
    @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&display=swap');
    
    :host {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      font-family: 'Crimson Pro', Georgia, serif;
      font-size: 1rem;
      color: #2c2c2c;
      vertical-align: middle;
    }
    
    :host([disabled]) {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .label {
      user-select: none;
    }
    
    .label-left {
      color: #666;
    }
    
    .label-right {
      color: #666;
    }
    
    :host([checked]) .label-right {
      color: #2c2c2c;
      font-weight: 600;
    }
    
    :host(:not([checked])) .label-left {
      color: #2c2c2c;
      font-weight: 600;
    }
    
    .track {
      position: relative;
      width: var(--track-width, 44px);
      height: var(--track-height, 24px);
      background: var(--toggle-track-color-off, #d4d0c8);
      border-radius: calc(var(--track-height, 24px) / 2);
      cursor: pointer;
      transition: background-color var(--toggle-transition-duration, 0.2s) ease;
      border: 1px solid rgba(0, 0, 0, 0.1);
    }
    
    :host([disabled]) .track {
      cursor: not-allowed;
    }
    
    :host([checked]) .track {
      background: var(--toggle-track-color-on, #8b7355);
      border-color: rgba(0, 0, 0, 0.15);
    }
    
    .thumb {
      position: absolute;
      /* Account for 1px border: total inset = 2px (1px padding + 1px border) */
      top: 50%;
      left: 2px;
      transform: translateY(-50%);
      width: calc(var(--track-height, 24px) - 6px);
      height: calc(var(--track-height, 24px) - 6px);
      background: var(--toggle-thumb-color, #fff);
      border-radius: 50%;
      box-shadow: var(--toggle-thumb-shadow, 0 1px 3px rgba(0, 0, 0, 0.2));
      transition: transform var(--toggle-transition-duration, 0.2s) ease, left var(--toggle-transition-duration, 0.2s) ease;
    }
    
    :host([checked]) .thumb {
      /* Move to right side: track width - thumb width - left offset - right padding */
      left: calc(var(--track-width, 44px) - var(--track-height, 24px) + 4px);
    }
    
    /* Focus styles */
    .track:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(139, 115, 85, 0.3);
    }
    
    .track:focus:not(:focus-visible) {
      box-shadow: none;
    }
    
    .track:focus-visible {
      box-shadow: 0 0 0 2px rgba(139, 115, 85, 0.3);
    }
    
    /* Size variants */
    :host([size="small"]) {
      --track-width: 32px;
      --track-height: 18px;
      font-size: 0.85rem;
    }
    
    :host([size="large"]) {
      --track-width: 56px;
      --track-height: 30px;
      font-size: 1.1rem;
    }
    
    /* Hover effect */
    @media (hover: hover) {
      .track:hover:not(:disabled) {
        border-color: rgba(0, 0, 0, 0.2);
      }
      
      :host([checked]) .track:hover:not(:disabled) {
        background: #7a6548;
      }
    }
    
    /* Touch device adjustments */
    @media (hover: none) {
      .track:active:not(:disabled) {
        border-color: rgba(0, 0, 0, 0.2);
      }
    }
  `;

  class ToggleSwitch extends HTMLElement {
    static get observedAttributes() {
      return ['checked', 'disabled', 'label-left', 'label-right', 'size'];
    }

    static formAssociated = true;

    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      
      // Form association
      if ('ElementInternals' in window) {
        this._internals = this.attachInternals?.();
      }
    }

    connectedCallback() {
      this.render();
      this._updateFormValue();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      if (name === 'checked') {
        this._updateFormValue();
      }
      
      // Re-render for label changes
      if (name === 'label-left' || name === 'label-right') {
        this.render();
      }
    }

    // === Attribute getters/setters ===

    get checked() {
      return this.hasAttribute('checked');
    }

    set checked(value) {
      if (value) {
        this.setAttribute('checked', '');
      } else {
        this.removeAttribute('checked');
      }
    }

    get disabled() {
      return this.hasAttribute('disabled');
    }

    set disabled(value) {
      if (value) {
        this.setAttribute('disabled', '');
      } else {
        this.removeAttribute('disabled');
      }
    }

    get labelLeft() {
      return this.getAttribute('label-left') || '';
    }

    get labelRight() {
      return this.getAttribute('label-right') || '';
    }

    get name() {
      return this.getAttribute('name') || '';
    }

    get value() {
      return this.getAttribute('value') || 'on';
    }

    // === Public API ===

    /**
     * Toggle the switch state
     * @returns {boolean} The new checked state
     */
    toggle() {
      if (this.disabled) return this.checked;
      
      this.checked = !this.checked;
      this._dispatchChange();
      return this.checked;
    }

    // === End Public API ===

    _handleClick(event) {
      if (this.disabled) return;
      
      this.toggle();
    }

    _handleKeydown(event) {
      if (this.disabled) return;
      
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        this.toggle();
      }
    }

    _dispatchChange() {
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        detail: { checked: this.checked }
      }));
    }

    _updateFormValue() {
      if (this._internals) {
        this._internals.setFormValue(this.checked ? this.value : null);
      }
    }

    _escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    render() {
      this.shadowRoot.innerHTML = `
        <style>${STYLES}</style>
        
        ${this.labelLeft ? `<span class="label label-left">${this._escapeHtml(this.labelLeft)}</span>` : ''}
        
        <div class="track" 
             role="switch" 
             tabindex="${this.disabled ? -1 : 0}"
             aria-checked="${this.checked}"
             aria-disabled="${this.disabled}">
          <div class="thumb"></div>
        </div>
        
        ${this.labelRight ? `<span class="label label-right">${this._escapeHtml(this.labelRight)}</span>` : ''}
      `;

      const track = this.shadowRoot.querySelector('.track');
      track.addEventListener('click', (e) => this._handleClick(e));
      track.addEventListener('keydown', (e) => this._handleKeydown(e));
    }
  }

  customElements.define('toggle-switch', ToggleSwitch);

  // Export for module usage
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ToggleSwitch };
  }
})();
